import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Formik, Form } from "formik";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import FormikDatePicker from "@/components/forms/FormikDatePicker";
import { CardWithActions, StyledButton } from "@/components";
import { TableWrapper, StyledTable } from "./StyledTable";
import {
  useGetAdminAttendanceTrackerQuery,
  useLazyGetDownloadTrackerReportExcelQuery,
} from "@/api/services/attendanceManagement/adminAttendanceReport/AdminAttendanceReport";
import { useFetchSettingsQuery } from "@/api/services/Settings/menu/Menu";
import { useFetchEmployeesByParentOrgQuery } from "@/api/services/employeeList/EmployeeList";
import SubmitButtonWithLoader from "@/components/forms/SubmitButtonWithLoader";
import { DownloadIcon } from "@/icons";
import * as XLSX from "xlsx";

const AttendanceTracker = () => {
  const [rows, setRows] = useState([]);
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const today = new Date();

  const formatDate = (date) => date.toLocaleDateString("en-CA"); // YYYY-MM-DD

  const getDefaultAppliedFilters = () => ({
    startDate: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    endDate: formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    empId: "",
    department: "",
    swipeMethod: "",
    workLocation: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(
    getDefaultAppliedFilters,
  );

  const {
    data: rowDetails = {},
    isLoading,
    isFetching,
  } = useGetAdminAttendanceTrackerQuery({
    startDate: appliedFilters.startDate,
    endDate: appliedFilters.endDate,
    empId: appliedFilters.empId,
    deparment: appliedFilters.department,
    swipeMethod: appliedFilters.swipeMethod,
    workLocation: appliedFilters.workLocation,
  });
  const [trackerDownload, { isLoading: isDownloading }] =
    useLazyGetDownloadTrackerReportExcelQuery();
  const { data: workLocationOptions = [] } = useFetchSettingsQuery({
    referenceName: "Work Location",
  });
  const { data: departmentOptions = [] } = useFetchSettingsQuery({
    referenceName: "Department",
  });
  const { data: swipeMethodOptions = [] } = useFetchSettingsQuery({
    referenceName: "Swipe Method",
  });
  const { data: fetchAllEmployees } = useFetchEmployeesByParentOrgQuery();

  const getFilterFormValues = (filters = appliedFilters) => ({
    startDate: new Date(filters.startDate),
    lastDate: new Date(filters.endDate),
    empId: filters.empId || "",
    swipeDetails: filters.swipeMethod || "",
    department: filters.department || "",
    workLocation: filters.workLocation || "",
  });

  const formValuesToApiParams = (values) => ({
    startDate: formatToYMD(values.startDate),
    endDate: formatToYMD(values.lastDate),
    empId: values.empId || "",
    deparment: values.department || "",
    swipeMethod: values.swipeDetails || "",
    workLocation: values.workLocation || "",
  });

  const handleDownloadReport = async (formValues) => {
    const params = formValuesToApiParams(formValues);
    const response = await trackerDownload({
      startDate: params.startDate,
      endDate: params.endDate,
      empId: params.empId,
      deparment: params.deparment,
      swipeMethod: params.swipeMethod,
      workLocation: params.workLocation,
    }).unwrap();
    // if (!sampleData || !(sampleData instanceof Blob)) {
    //   return;
    // }
    const url = URL.createObjectURL(response);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Attendance Tracker Report.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const loadings = isLoading || isFetching;

  const formatToYMD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!rowDetails?.data) return;

    const structured = Object.entries(rowDetails.data).map(
      ([deptName, deptData]) => ({
        department: deptName,
        ...deptData,
      }),
    );

    setRows(structured);
  }, [rowDetails]);

  const prepareExcelData = (apiData) => {
    const rows = [];
    Object.entries(apiData).forEach(([departmentName, deptData]) => {
      deptData?.members?.forEach((emp) => {
        // ✅ FIX HERE
        const dates = emp?.individualReport || {};
        // If employee has NO date data
        if (Object.keys(dates).length === 0) {
          rows.push({
            Department: departmentName,
            "Employee ID": emp.empId,
            "Employee Name": emp.empName,
            // Date: "-",

            "In Punch Count": emp.empAvgInPunch ?? "-",
            "Out Punch Count": emp.empAvgOutPunch ?? "-",
            "Total Hours": emp.empAvgLoginHourse ?? "-",
            "Productive Hours": emp.empProductiveHousers ?? "-",
            "Break Hours": emp.empBreakHourse ?? "-",

            "Present Days": emp.empPresentDayCount,
            "Avg Login Hours": emp.empAvgLoginHourse,
            "Employee Count": emp.empCount,

            "Dept Overall Count": deptData.overallEmployeeCount,
            "Dept Present Count": deptData.totalEmployeePresentCount,
            "Dept Avg Login": deptData.avgLoginHourse,
          });
          return;
        }

        // ✅ Date-wise rows (single sheet)
        Object.values(dates).forEach((day) => {
          rows.push({
            Department: departmentName,
            "Employee ID": emp.empId,
            "Employee Name": emp.empName,
            Date: day.date,

            "In Punch Count": day.inPunchCount,
            "Out Punch Count": day.outPunchCount,
            "Total Hours": day.totalHrs,
            "Productive Hours": day.productiveHrs,
            "Break Hours": day.breakHrs,

            "Present Days": emp.empPresentDayCount,
            "Avg Login Hours": emp.empAvgLoginHourse,
            "Employee Count": emp.empCount,

            "Dept Overall Count": deptData.overallEmployeeCount,
            "Dept Present Count": deptData.totalEmployeePresentCount,
            "Dept Avg Login": deptData.avgLoginHourse,
          });
        });
      });
    });

    return rows;
  };

  const downloadExcel = () => {
    const excelData = prepareExcelData(rowDetails.data); // <-- your API response object

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Attendance Dept Wise Report",
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Attendance_Date_Wise_Report.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <CardWithActions
      headerTitle="Attendance Employees Tracker"
      headerActions={[]}
    >
      {/* Filter Section */}
      <Box mb={2}>
        <Formik
          initialValues={getFilterFormValues()}
          enableReinitialize
          onSubmit={(values) => {
            setExpandedDepartments({});
            setAppliedFilters({
              startDate: formatToYMD(values.startDate),
              endDate: formatToYMD(values.lastDate),
              empId: values.empId || "",
              department: values.department || "",
              swipeMethod: values.swipeDetails || "",
              workLocation: values.workLocation || "",
            });
          }}
        >
          {({ resetForm, values }) => (
            <Form>
              <Grid
                container
                alignItems="center"
                spacing={2}
                // justifyContent="space-between"
              >
                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <FormikDatePicker
                    type="date"
                    name="startDate"
                    label="Start Date"
                    views={["year", "month", "day"]}
                    placeholder="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <FormikDatePicker
                    type="date"
                    name="lastDate"
                    label="End Date"
                    views={["year", "month", "day"]}
                    placeholder="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <FormikSelectInput
                    search={true}
                    name="empId"
                    options={fetchAllEmployees?.map((emp) => ({
                      value: emp.empId,
                      label: emp.employeeName,
                    }))}
                    label="Employee"
                    clear={true}
                    placeholder="Select Employee"
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <FormikSelectInput
                    search={true}
                    name="workLocation"
                    options={workLocationOptions?.map((emp) => ({
                      value: emp.value,
                      label: emp.name,
                    }))}
                    label="Work Location"
                    clear={true}
                    placeholder="Select Work Location"
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <FormikSelectInput
                    search={true}
                    name="department"
                    options={departmentOptions?.map((emp) => ({
                      value: emp.value,
                      label: emp.name,
                    }))}
                    label="Department"
                    clear={true}
                    placeholder="Select Department"
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <FormikSelectInput
                    search={true}
                    name="swipeDetails"
                    options={swipeMethodOptions?.map((emp) => ({
                      value: emp.value,
                      label: emp.name,
                    }))}
                    label="Swipe Details"
                    clear={true}
                    placeholder="Select Swipe Details"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <Stack
                    spacing={1}
                    sx={{ mt: "25px" }}
                    direction={{ xs: "column", sm: "row" }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <StyledButton
                      sx={{
                        p: (theme) => theme.spacing(1, 2),
                        borderRadius: (theme) => theme.shape.s,
                      }}
                      type="submit"
                      label="Submit"
                      variant="contained"
                      size="small"
                    />
                    <StyledButton
                      sx={{
                        p: (theme) => theme.spacing(1, 2),
                        borderRadius: (theme) => theme.shape.s,
                      }}
                      type="reset"
                      label="Reset"
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        const defaults = getDefaultAppliedFilters();
                        setAppliedFilters(defaults);
                        resetForm({ values: getFilterFormValues(defaults) });
                        setExpandedDepartments({});
                      }}
                    />
                    {appliedFilters.department &&
                      Object.keys(rowDetails?.data || {}).length > 0 && (
                        <SubmitButtonWithLoader
                          type="button"
                          submittingText="Downloading..."
                          variant="contained"
                          onClick={() => downloadExcel()}
                          startIcon={<DownloadIcon />}
                        >
                          Date Wise Excel
                        </SubmitButtonWithLoader>
                      )}
                    <SubmitButtonWithLoader
                      type="button"
                      submittingText="Downloading..."
                      variant="contained"
                      loading={isDownloading}
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadReport(values)}
                    >
                      Download Tracker
                    </SubmitButtonWithLoader>
                  </Stack>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>

      {/* Main Table */}
      <TableWrapper>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell>Department</TableCell>
              <TableCell>Employee Name</TableCell>
              <TableCell>Employee Code</TableCell>
              <TableCell>Employee Count</TableCell>
              <TableCell>Present Days</TableCell>
              <TableCell>Regularized Days</TableCell>
              <TableCell>Avg In</TableCell>
              <TableCell>Avg Out</TableCell>
              <TableCell>Avg Login Hrs</TableCell>
              <TableCell>Productive Hrs</TableCell>
              <TableCell>Break Hrs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadings ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    py={3}
                  >
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    {/* <span>Loading data...</span> */}
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              rows.map((dept) => {
                const isDeptExpanded = !!expandedDepartments[dept.department];
                return (
                  <React.Fragment key={dept.department}>
                    <TableRow
                      sx={{ backgroundColor: "#f7f6f3", cursor: "pointer" }}
                      onClick={() =>
                        setExpandedDepartments((prev) => ({
                          ...prev,
                          [dept.department]: !prev[dept.department],
                        }))
                      }
                    >
                      <TableCell>
                        <IconButton size="small">
                          {isDeptExpanded ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>{" "}
                        <strong>{dept.department}</strong>
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>{dept.overallEmployeeCount}</TableCell>
                      <TableCell>{dept.totalEmployeePresentCount}</TableCell>
                      <TableCell>
                        {dept.totalEmployeeRegularizationCount}
                      </TableCell>
                      <TableCell>{dept.avgInPunchCount}</TableCell>
                      <TableCell>{dept.avgOutPunchCount}</TableCell>
                      <TableCell>{dept.avgLoginHourse}</TableCell>
                      <TableCell>{dept.totalProductiveHousers}</TableCell>
                      <TableCell>{dept.totalBreakHousers}</TableCell>
                    </TableRow>
                    {isDeptExpanded &&
                      dept.members.map((emp, i) => {
                        const rowKey = `${dept.department}-${emp.empId || i}`;

                        return (
                          <React.Fragment key={rowKey}>
                            <TableRow>
                              <TableCell />
                              <TableCell>{emp.empName}</TableCell>
                              <TableCell>{emp.empId}</TableCell>
                              <TableCell>{emp.empCount}</TableCell>
                              <TableCell>{emp.empPresentDayCount}</TableCell>
                              <TableCell>{emp.empRegularizadCount}</TableCell>
                              <TableCell>{emp.empAvgInPunch}</TableCell>
                              <TableCell>{emp.empAvgOutPunch}</TableCell>
                              <TableCell>{emp.empAvgLoginHourse}</TableCell>
                              <TableCell>{emp.empProductiveHousers}</TableCell>
                              <TableCell>{emp.empBreakHourse}</TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </StyledTable>
      </TableWrapper>
    </CardWithActions>
  );
};

export default AttendanceTracker;
